import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import useOutsideClick from 'common/hooks/useOutsideClick';
import { SortType } from 'common/types/chart';
import { LatanaText } from '../LatanaText';
import Icon from '../Icon';
import { DropdownAlign, DropdownWithIconProps } from 'common/types/common';
import {
  Container,
  Description,
  DropDownContainer,
  DropDownListItem,
  DropDownListWrapper,
  IconItem,
  Label,
  LabelWrapper,
  ListItemLabelWrapper,
  RadioButtonCircle,
  SelectedItem,
  SelectedItemInnerContainer,
} from './styled';
import { isObjectWithKey } from './utils';

const DEFAULT_TOP_GAP = 34;

const Dropdown = <T,>({
  label,
  testId,
  options,
  topLabel,
  onSelect,
  toolbarIcon,
  selectedValue,
  noHover = false,
  defaultText = '',
  withArrow = false,
  withBorder = false,
  inDashboard = false,
  renderInBody = false,
  withTopLabel = false,
  extraPadding = false,
  showDefaultText = false,
  showIconAndLabel = false,
  noActiveDefaultText = false,
  align = DropdownAlign.LEFT,
  noLabel = false,
  borderRadius,
  height,
  borderColor,
  colour,
  fontWeight,
  padding,
  centeredIconTrigger = false,
}: DropdownWithIconProps<T>) => {
  const [elementStyles, setElementStyles] = useState({});
  const [showDropDown, setShowDropDown] = useState(false);
  const contentRef = React.useRef<HTMLDivElement>() as React.MutableRefObject<HTMLDivElement>;

  React.useEffect(() => {
    const containerElement = contentRef.current;

    if (containerElement && showDropDown && renderInBody) {
      const { top: topPosition, right, left: leftPosition } = containerElement.getBoundingClientRect();
      const rightPosition = window.innerWidth - right;

      setElementStyles({
        top: `${topPosition + DEFAULT_TOP_GAP}px`,
        right: align === DropdownAlign.RIGHT ? `${rightPosition}px` : 'initial',
        left: align === DropdownAlign.LEFT ? `${leftPosition}px` : 'initial',
      });
    }
  }, [showDropDown, align, renderInBody]);

  useOutsideClick(contentRef, () => setShowDropDown(false));

  const onOptionSelect = React.useCallback(
    (value: unknown) => () => {
      onSelect(value);
    },
    [onSelect],
  );

  const selectedOption = React.useMemo(() => {
    if (!selectedValue || !options) return null;

    return options.find(({ value }) => {
      if (isObjectWithKey(value) && isObjectWithKey(selectedValue)) {
        return value.key === selectedValue.key;
      }

      return value === selectedValue;
    });
  }, [options, selectedValue]);

  const renderOptions = React.useMemo(() => {
    return (
      <DropDownListWrapper align={align} style={elementStyles}>
        {!noLabel && (
          <Label variant='body2' color='black25'>
            {label}
          </Label>
        )}
        {options.map(({ value, disabled, icon, label: optionLabel, description }) => (
          <DropDownListItem
            aria-disabled={disabled}
            key={`dropdown-item-${value}-${optionLabel}`}
            onMouseDown={disabled ? undefined : onOptionSelect(value)}
          >
            <RadioButtonCircle selected={selectedValue === value} />
            <ListItemLabelWrapper>
              {optionLabel}
              {description && <Description>{description}</Description>}
            </ListItemLabelWrapper>
            {icon && (
              <SelectedItem>
                <IconItem icon={icon} />
              </SelectedItem>
            )}
          </DropDownListItem>
        ))}
      </DropDownListWrapper>
    );
  }, [options, selectedValue, align, label, onOptionSelect, elementStyles, noLabel]);

  const renderOptionsPortal = React.useMemo(() => {
    return createPortal(renderOptions, document.body);
  }, [renderOptions]);

  const renderWithIconAndLabel = React.useMemo(() => {
    const isSortTypeDefault = selectedOption?.value === SortType.DEFAULT;
    const itemTitle = (isObjectWithKey(selectedOption?.value) ? selectedOption?.value.type : selectedOption?.value) as string;
    return (
      <React.Fragment>
        {isSortTypeDefault && <SelectedItem>{inDashboard ? <IconItem icon={toolbarIcon} /> : defaultText}</SelectedItem>}
        {selectedOption?.icon && !isSortTypeDefault && (
          <SelectedItem active={true} title={itemTitle}>
            {inDashboard ? (
              <IconItem icon={toolbarIcon} withLabel />
            ) : (
              <React.Fragment>
                <IconItem icon={selectedOption.icon} withLabel /> {selectedOption?.label}
              </React.Fragment>
            )}
          </SelectedItem>
        )}
      </React.Fragment>
    );
  }, [defaultText, inDashboard, selectedOption, toolbarIcon]);

  const renderBasic = React.useMemo(() => {
    const { icon: selectedIcon, label: selectedLabel } = selectedOption || {};
    const isCenteredIconTrigger = centeredIconTrigger;

    const isShownAsActive = noActiveDefaultText ? !!selectedLabel : true;

    const renderIconItem = (icon: string, withLabel = true) => (
      <IconItem icon={icon} withLabel={withLabel} centeredIconTrigger={isCenteredIconTrigger} />
    );
    const renderLabel = () => (showDefaultText && !selectedLabel ? label : selectedLabel);

    return (
      <SelectedItem
        active={isShownAsActive}
        noHover={noHover}
        extraPadding={extraPadding}
        height={height}
        padding={padding}
        color={colour}
        noActiveDefaultText={noActiveDefaultText}
        fontWeight={fontWeight}
        centeredIconTrigger={isCenteredIconTrigger}
      >
        <SelectedItemInnerContainer
          noActiveDefaultText={noActiveDefaultText}
          active={isShownAsActive}
          color={colour}
          fontWeight={fontWeight}
          centeredIconTrigger={isCenteredIconTrigger}
        >
          {inDashboard ? renderIconItem(toolbarIcon) : selectedIcon ? renderIconItem(selectedIcon) : renderLabel()}
        </SelectedItemInnerContainer>
        {withArrow && <Icon icon='arrow-chevron-down' color={selectedLabel ? 'latana500' : 'gray400'} />}
      </SelectedItem>
    );
  }, [
    inDashboard,
    label,
    selectedOption,
    showDefaultText,
    toolbarIcon,
    noActiveDefaultText,
    centeredIconTrigger,
    noHover,
    extraPadding,
    withArrow,
    height,
    padding,
    colour,
    fontWeight,
  ]);

  return (
    <Container>
      {withTopLabel && (
        <LabelWrapper>
          <LatanaText variant='L1' color='gray900'>
            {topLabel}
          </LatanaText>
        </LabelWrapper>
      )}
      <DropDownContainer
        ref={contentRef}
        data-testid={testId}
        onClick={() => setShowDropDown(!showDropDown)}
        withBorder={withBorder}
        active={!!selectedOption}
        borderRadius={borderRadius}
        height={height}
        borderColor={borderColor}
      >
        {showIconAndLabel ? renderWithIconAndLabel : renderBasic}
        {showDropDown && (renderInBody ? renderOptionsPortal : renderOptions)}
      </DropDownContainer>
    </Container>
  );
};

export default Dropdown;
